// @ts-nocheck
import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

// FontAwesome
import {
    faCheckCircle,
    faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Components
import PageTitle from "components/Common/PageTitle/PageTitle";
import Tabs from "components/Common/Tabs/Tabs";
import Review from "components/Screens/BQMQ/Setup/Review/Review";
import SelectAnalyst from "components/Screens/BQMQ/Setup/SelectAnalyst/SelectAnalyst";
import SelectCompany from "components/Screens/BQMQ/Setup/SelectCompany/SelectCompany";
import SelectDate from "components/Screens/BQMQ/Setup/SelectDate/SelectDate";
import SelectHost from "components/Screens/BQMQ/Setup/SelectHost/SelectHost";
import SelectUsers from "components/Screens/BQMQ/Setup/SelectUsers/SelectUsers";
import SelectVoteType from "components/Screens/BQMQ/Setup/VoteType/VoteType";

// Types
import { VoteTypes } from "types/VoteTypes";

// Utils
import { applyTimeZoneOffset } from "utils/common.utils";
import { checkForRecentCompanySessions } from "utils/bqmq.utils";

// Styles
import { useBQMQ } from "context/BQMQ.context";
import CreateSessionContainer from "./CreateSession.styles";
import Modal from "components/Common/Modal/Modal";
import ButtonSmall from "components/Common/ButtonSmall/ButtonSmall";
import Button from "components/Common/Button/Button";
import { faClose } from "@fortawesome/free-solid-svg-icons";

export default function CreateSession() {
    const location = useLocation();
    const [tab] = useState<
        "company" | "chair" | "analyst" | "users" | "review" | ""
    >("company");
    const [company, setCompany] = useState(null);
    const [analyst, setAnalyst] = useState("");
    const [chair, setChair] = useState("");
    const [users, setUsers] = useState<BQMQUser[]>([]);
    const [date, setDate] = useState<Date | null>(null);
    const [voteType, setVoteType] = useState<VoteTypes | "">("");

    const { setMyVotes, analysts, sessions, companies } = useBQMQ();

    const [scheduledEvents, setScheduledEvents] = useState<
        ScheduledEvent[] | []
    >([]);
    const [eventArray, setEventArray] = useState<any[]>([]);

    const [showRecentSessionModal, setShowRecentSessionModal] = useState(false);
    const [recentSessionLink, setRecentSessionLink] = useState<string>("");

    // Function to check for recent sessions and redirect if found
    const handleRecentSessionCheck = useCallback(async (companyId: string) => {
        const recentSession = await checkForRecentCompanySessions(companyId);
        if (recentSession) {
            const link = `/company/voting/edit-session?id=${recentSession.id}`;
            setRecentSessionLink(link);
            setShowRecentSessionModal(true);
            return true;
        }
        return false; // No redirect happened
    }, []);

    const getUserList = useCallback(async () => {
        setUsers(
            analysts
                .sort((a: any, b: any) => a.label.localeCompare(b.label))
                .map((user: any) => ({
                    name: user.label,
                    id: user.value,
                    active: false,
                    completed: false,
                    invited: true,
                    core: false,
                }))
        );
    }, [analysts]);

    const getScheduledEvents = useCallback(async () => {
        const scheduledEvents = sessions.map((session: any) => ({
            title: `${applyTimeZoneOffset(session.start_date, "HH:mm")}: ${
                session.company.name
            }`,
            start: session.date,
            end: session.date,
            allDay: true,
        }));

        date
            ? setEventArray([
                  ...scheduledEvents,
                  {
                      title: `> ${applyTimeZoneOffset(date, "HH:mm")}: ${
                          company ? company.label : "NEW SESSION"
                      }`,
                      start: date,
                      end: date,
                      allDay: true,
                  },
              ])
            : setEventArray(scheduledEvents);

        setScheduledEvents(scheduledEvents);
    }, [sessions, date, company]);

    // Initialize company from URL params
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const companyId = params.get("company_id");
        if (companyId && companies.length > 0) {
            const selectedCompany = companies.find(
                (comp: any) => String(comp.value) === companyId
            );
            if (selectedCompany) {
                setCompany(selectedCompany);
            }
        }
    }, [location.search, companies]);

    useEffect(() => {
        const existingVotes = localStorage.getItem("bqmq");
        if (existingVotes) {
            localStorage.removeItem("bqmq");
            setMyVotes({});
        }
        getUserList();
    }, [getUserList, setMyVotes]);

    useEffect(() => {
        getScheduledEvents();
    }, [company, getScheduledEvents]);

    // Check for existing sessions when company is selected
    useEffect(() => {
        if (company && (company.id || company.value)) {
            const companyId = company.id || company.value;
            handleRecentSessionCheck(companyId);
        }
    }, [company, handleRecentSessionCheck]);

    return (
        <CreateSessionContainer className={"pageContainer"}>
            <PageTitle title="CREATE NEW FOCUS LIST SESSION" />

            {showRecentSessionModal && (
                <Modal onClose={() => setShowRecentSessionModal(false)}>
                    <div className="main">
                        <div className="text">
                            <h3>NOTICE</h3>
                            <p>
                                There was a voting session for this company over
                                the last 1.5 month. It can be accessed and
                                edited here:{" "}
                                <Link to={recentSessionLink}>here</Link>
                            </p>
                        </div>
                    </div>
                    <div className="actions">
                        <Button
                            text="CLOSE"
                            color="flat"
                            click={() => setShowRecentSessionModal(false)}
                        />
                    </div>
                    <div
                        className={"btn-close-container"}
                        style={{ position: "absolute", top: 8, right: 8 }}
                    >
                        <ButtonSmall
                            color={"flat"}
                            text={"Close"}
                            icon={faClose}
                            click={() => setShowRecentSessionModal(false)}
                        />
                    </div>
                </Modal>
            )}

            <Tabs
                defaultTab={tab}
                dontChangeUrl
                tabs={[
                    {
                        title: "COMPANY",
                        value: "company",
                        content: (
                            <SelectCompany
                                company={company}
                                setCompany={setCompany}
                            />
                        ),
                        icon: (
                            <FontAwesomeIcon
                                className={
                                    "tabIcon " + (company ? "complete" : "")
                                }
                                icon={company ? faCheckCircle : faTimesCircle}
                                fixedWidth
                            />
                        ),
                    },
                    {
                        title: "CHAIR",
                        value: "chair",
                        content: (
                            <SelectHost
                                chair={chair}
                                setChair={setChair}
                                users={users}
                                setUsers={setUsers}
                            />
                        ),
                        icon: (
                            <FontAwesomeIcon
                                className={
                                    "tabIcon " + (chair ? "complete" : "")
                                }
                                icon={chair ? faCheckCircle : faTimesCircle}
                                fixedWidth
                            />
                        ),
                    },
                    {
                        title: "PRESENTER",
                        value: "analyst",
                        content: (
                            <SelectAnalyst
                                analyst={analyst}
                                setAnalyst={setAnalyst}
                                users={users}
                                setUsers={setUsers}
                            />
                        ),
                        icon: (
                            <FontAwesomeIcon
                                className={
                                    "tabIcon " + (analyst ? "complete" : "")
                                }
                                icon={analyst ? faCheckCircle : faTimesCircle}
                                fixedWidth
                            />
                        ),
                    },
                    {
                        title: "ATTENDEES",
                        value: "users",
                        content: (
                            <SelectUsers
                                users={users}
                                setUsers={setUsers}
                                chair={chair}
                                analyst={analyst}
                            />
                        ),
                        icon: (
                            <FontAwesomeIcon
                                className={
                                    "tabIcon " +
                                    (users.some(
                                        (user: any) => user.core === true
                                    ) &&
                                    users.some(
                                        (user: any) => user.core === true
                                    )
                                        ? "complete"
                                        : "")
                                }
                                icon={
                                    users.some(
                                        (user: any) => user.core === true
                                    )
                                        ? faCheckCircle
                                        : faTimesCircle
                                }
                                fixedWidth
                            />
                        ),
                    },
                    {
                        title: "DATE",
                        value: "date",
                        content: (
                            <SelectDate
                                company={company}
                                date={date}
                                setDate={setDate}
                                eventArray={eventArray}
                                setEventArray={setEventArray}
                                scheduledEvents={scheduledEvents}
                            />
                        ),
                        icon: (
                            <FontAwesomeIcon
                                className={
                                    "tabIcon " + (date ? "complete" : "")
                                }
                                icon={date ? faCheckCircle : faTimesCircle}
                                fixedWidth
                            />
                        ),
                    },
                    {
                        title: "VOTE TYPE",
                        value: "vote",
                        content: (
                            <SelectVoteType
                                voteType={voteType}
                                setVoteType={setVoteType}
                            />
                        ),
                        icon: (
                            <FontAwesomeIcon
                                className={
                                    "tabIcon " + (voteType ? "complete" : "")
                                }
                                icon={voteType ? faCheckCircle : faTimesCircle}
                                fixedWidth
                            />
                        ),
                    },
                    {
                        title: "REVIEW & SAVE",
                        value: "review",
                        content: (
                            <Review
                                company={company}
                                chair={chair}
                                analyst={analyst}
                                users={users}
                                date={date}
                                factorID={voteType}
                            />
                        ),
                    },
                ]}
            />
        </CreateSessionContainer>
    );
}
